/**
 * F2 — Setlist → projection queue (pure).
 *
 * Materializes the songs of an F1 setlist as DRAFTS of
 * `projection_item` (kind `song`): the full lyrics go to `content` (the
 * presenter pages them by stanza with `splitSlides`) and the music card
 * (key of the occasion, tempo, author) stays in `notes` — visible ONLY in
 * the presenter view, never in the projector output.
 *
 * Copying (rather than referencing) is an ISOLATION decision: the songs
 * live in the praise team's `songs` scope; the projector and the projection
 * director only have the `projection` capability of their venue. On import,
 * whoever has BOTH capabilities materializes the content inside the
 * projection scope and the projector device does not need (nor receive) the
 * music library.
 */

import { effectiveKey, orderSetlistSongs, type SetlistSongLike } from "../music/setlist";

export interface ProjectableSongLike {
  id: string;
  title: string | null;
  author?: string | null;
  song_key?: string | null;
  tempo?: number | null;
  lyrics?: string | null;
  deleted_at?: string | null;
}

/** `projection_item` draft ready to insert (the id is set by the UI). */
export interface ProjectionDraft {
  kind: "song";
  title: string;
  content: string | null;
  notes: string | null;
  sortOrder: number;
}

function songNotes(
  song: ProjectableSongLike,
  keyOverride: string | null | undefined,
): string | null {
  const parts: string[] = [];
  const key = effectiveKey(song.song_key, keyOverride);
  if (key) {
    parts.push(`♪ ${key}`);
  }
  if (song.tempo) {
    parts.push(`${song.tempo} bpm`);
  }
  if (song.author?.trim()) {
    parts.push(song.author.trim());
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Converts the lines of a setlist (ordered, with their per-occasion key)
 * into projection drafts. Lines whose song is not synced or is in the
 * trash are OMITTED (the director only imports what their device can see).
 * `startOrder` allows queueing behind what is already projectable.
 */
export function setlistToProjectionDrafts(
  setlistSongs: readonly (SetlistSongLike & { setlist_id?: string | null })[],
  songs: readonly ProjectableSongLike[],
  startOrder = 0,
): ProjectionDraft[] {
  const byId = new Map(songs.map((song) => [song.id, song]));
  const drafts: ProjectionDraft[] = [];
  for (const line of orderSetlistSongs(setlistSongs)) {
    const song = line.song_id ? byId.get(line.song_id) : undefined;
    if (!song || song.deleted_at) {
      continue;
    }
    drafts.push({
      kind: "song",
      title: song.title?.trim() || "—",
      content: song.lyrics?.trim() || null,
      notes: songNotes(song, line.key_override),
      sortOrder: startOrder + drafts.length + 1,
    });
  }
  return drafts;
}
